import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, CheckCircle2, RefreshCw, ChevronDown, GitBranch, List, ClipboardList, Check } from 'lucide-react';

import {
  fetchStatusMapItemsThunk,
  fetchStatusMapComponentsThunk,
  updateStatusMapItemThunk,
  deleteStatusMapItemThunk,
} from '../../../redux/admin/statusMapActionThunks';
import { createTaskThunk } from '../../../redux/admin/tasksActionThunks';
import ArchitectureDiagram from './ArchitectureDiagram';

const STATUSES = ['fixed', 'open', 'deferred'];

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30', dot: 'bg-orange-500' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-500' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
};
const FIXED_COLOR = { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30', dot: 'bg-cyan-500' };

const effectiveKey = (item) => (item.status === 'fixed' ? 'fixed' : item.severity);
const colorFor = (item) => (item.status === 'fixed' ? FIXED_COLOR : SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.low);

/**
 * Live, admin-only Architecture Status Map -- the in-app twin of the standalone
 * HTML report generated during the security-hardening pass, now backed by the
 * real architecture_status_items table (see crud_architecture_status.py /
 * architecture_status_endpoint.py) instead of a hardcoded JS array, so it stays
 * "alive": an admin can flip an item's status, delete stale entries, or add new
 * findings straight from here, and every other admin sees the update immediately.
 */
const AdminStatusMapComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { items, components, loading, errors } = useSelector((state) => state.statusMap);

  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');
  const [activeComponents, setActiveComponents] = useState(new Set(['all']));
  const [activeCategory, setActiveCategory] = useState('all'); // 'all' | 'engineering' | 'regulatory'
  const [viewMode, setViewMode] = useState('diagram'); // 'diagram' | 'list'

  useEffect(() => {
    dispatch(fetchStatusMapItemsThunk());
    dispatch(fetchStatusMapComponentsThunk());
  }, [dispatch]);

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    pageBg: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700/60' : 'hover:bg-gray-50',
    selectedBg: darkMode ? 'bg-gray-700/60' : 'bg-blue-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    chipBg: darkMode ? 'bg-gray-700/50' : 'bg-gray-100',
  };

  const stats = useMemo(() => {
    const s = { critical: 0, high: 0, medium: 0, low: 0, fixed: 0, regulatoryOpen: 0 };
    items.forEach((i) => {
      if (i.status === 'fixed') s.fixed++;
      else s[i.severity] = (s[i.severity] || 0) + 1;
      if (i.category === 'regulatory' && i.status !== 'fixed') s.regulatoryOpen++;
    });
    return s;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const compOk = activeComponents.has('all') || activeComponents.has(item.component);
      if (!compOk) return false;
      if (activeCategory !== 'all' && item.category !== activeCategory) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.component.toLowerCase().includes(q) ||
        (item.files || []).some((f) => f.toLowerCase().includes(q))
      );
    });
  }, [items, activeComponents, activeCategory, query]);

  const grouped = useMemo(() => {
    const g = { fixed: [], critical: [], high: [], medium: [], low: [] };
    filtered.forEach((item) => g[effectiveKey(item)].push(item));
    return g;
  }, [filtered]);

  const selected = items.find((i) => i.id === selectedId) || null;

  const toggleComponent = (c) => {
    setActiveComponents((prev) => {
      const next = new Set(prev);
      if (c === 'all') return new Set(['all']);
      next.delete('all');
      if (next.has(c)) next.delete(c);
      else next.add(c);
      if (next.size === 0) next.add('all');
      return next;
    });
  };

  const handleStatusChange = (item, newStatus) => {
    dispatch(updateStatusMapItemThunk({ itemId: item.id, updates: { status: newStatus } }));
  };

  const handleDelete = (item) => {
    if (!window.confirm(t('admin.statusMap.deleteConfirm', { title: item.title }))) return;
    dispatch(deleteStatusMapItemThunk(item.id));
    if (selectedId === item.id) setSelectedId(null);
  };

  const handleViewIssuesForNode = (relatedComponents) => {
    if (!relatedComponents || relatedComponents.length === 0) return; // healthy node, nothing to jump to
    setActiveComponents(new Set(relatedComponents));
    setQuery('');
    setViewMode('list');
  };

  const GROUP_ORDER = [
    ['fixed', t('admin.statusMap.groupLabels.fixed')],
    ['critical', t('admin.statusMap.groupLabels.critical')],
    ['high', t('admin.statusMap.groupLabels.high')],
    ['medium', t('admin.statusMap.groupLabels.medium')],
    ['low', t('admin.statusMap.groupLabels.low')],
  ];

  return (
    <div className={`rounded-lg border ${themeClasses.border} ${themeClasses.cardBg} overflow-hidden`}>
      {/* Header / stats */}
      <div className={`p-4 border-b ${themeClasses.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className={`text-lg font-bold ${themeClasses.text}`}>{t('admin.statusMap.title')}</h3>
            <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.statusMap.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center rounded-md border ${themeClasses.border} overflow-hidden`}>
              <button
                onClick={() => setViewMode('diagram')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'diagram' ? 'bg-blue-600 text-white' : `${themeClasses.chipBg} ${themeClasses.textSecondary} ${themeClasses.hoverBg}`
                }`}
              >
                <GitBranch className="w-3.5 h-3.5" />
                {t('admin.statusMap.diagram')}
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'list' ? 'bg-blue-600 text-white' : `${themeClasses.chipBg} ${themeClasses.textSecondary} ${themeClasses.hoverBg}`
                }`}
              >
                <List className="w-3.5 h-3.5" />
                {t('admin.statusMap.list')}
              </button>
            </div>
            <button
              onClick={() => dispatch(fetchStatusMapItemsThunk())}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${themeClasses.chipBg} ${themeClasses.textSecondary} ${themeClasses.hoverBg}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading.items ? 'animate-spin' : ''}`} />
              {t('admin.statusMap.refresh')}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <StatChip label={t('admin.statusMap.stats.total')} value={items.length} theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.fixed')} value={stats.fixed} colorClass="text-cyan-400" theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.critical')} value={stats.critical} colorClass="text-red-400" theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.high')} value={stats.high} colorClass="text-orange-400" theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.medium')} value={stats.medium} colorClass="text-amber-400" theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.low')} value={stats.low} colorClass="text-emerald-400" theme={themeClasses} />
          <StatChip label={t('admin.statusMap.stats.regulatoryOpen')} value={stats.regulatoryOpen} colorClass="text-violet-400" theme={themeClasses} />
        </div>

        {viewMode === 'list' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 ${themeClasses.textMuted}`} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('admin.statusMap.searchPlaceholder')}
                  className={`w-full pl-8 pr-3 py-2 rounded-md text-sm ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-2">
              <FilterChip label={t('admin.statusMap.categoryAll')} active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} theme={themeClasses} />
              <FilterChip label={t('admin.statusMap.categoryEngineering')} active={activeCategory === 'engineering'} onClick={() => setActiveCategory('engineering')} theme={themeClasses} />
              <FilterChip label={t('admin.statusMap.categoryRegulatory')} active={activeCategory === 'regulatory'} onClick={() => setActiveCategory('regulatory')} theme={themeClasses} />
            </div>

            <div className="flex flex-wrap gap-1.5">
              <FilterChip label={t('admin.statusMap.allComponents')} active={activeComponents.has('all')} onClick={() => toggleComponent('all')} theme={themeClasses} />
              {components.map((c) => (
                <FilterChip key={c} label={c} active={activeComponents.has(c)} onClick={() => toggleComponent(c)} theme={themeClasses} />
              ))}
            </div>
          </>
        )}
      </div>

      {errors.items && (
        <div className="p-3 text-sm text-red-400 bg-red-500/10 border-b border-red-500/30">{errors.items}</div>
      )}

      {viewMode === 'diagram' ? (
        <div className="p-4">
          <ArchitectureDiagram items={items} darkMode={darkMode} onViewIssues={handleViewIssuesForNode} />
        </div>
      ) : (
      /* Body: sidebar + detail */
      <div className="flex" style={{ minHeight: '480px' }}>
        <div className={`w-full sm:w-80 flex-shrink-0 border-r ${themeClasses.border} overflow-y-auto`} style={{ maxHeight: '640px' }}>
          {loading.items && items.length === 0 ? (
            <div className={`p-6 text-center text-sm ${themeClasses.textMuted}`}>{t('admin.statusMap.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className={`p-6 text-center text-sm ${themeClasses.textMuted}`}>{t('admin.statusMap.noMatches')}</div>
          ) : (
            GROUP_ORDER.map(([key, label]) =>
              grouped[key].length === 0 ? null : (
                <div key={key}>
                  <div className={`px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide ${themeClasses.textMuted} sticky top-0 ${themeClasses.cardBg}`}>
                    {label} ({grouped[key].length})
                  </div>
                  {grouped[key].map((item) => {
                    const c = colorFor(item);
                    return (
                      <button
                        key={item.id}
                        onClick={() => setSelectedId(item.id)}
                        className={`block w-full text-left px-4 py-2.5 border-b ${themeClasses.border} ${themeClasses.hoverBg} ${
                          selectedId === item.id ? themeClasses.selectedBg : ''
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${themeClasses.textMuted}`}>{item.component}</span>
                          {item.category === 'regulatory' && (
                            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/30">
                              {t('admin.statusMap.regulatoryBadge')}
                            </span>
                          )}
                        </div>
                        <div className={`text-sm font-medium leading-snug ${themeClasses.text}`}>{item.title}</div>
                        {item.new_this_pass && (
                          <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-violet-500 to-blue-500 text-white">
                            {t('admin.statusMap.thisPassBadge')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            )
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5" style={{ maxHeight: '640px' }}>
          {!selected ? (
            <div className={`text-sm ${themeClasses.textMuted}`}>{t('admin.statusMap.selectItemPrompt')}</div>
          ) : (
            <StatusMapDetail
              item={selected}
              theme={themeClasses}
              onStatusChange={(s) => handleStatusChange(selected, s)}
              onDelete={() => handleDelete(selected)}
              mutating={loading.mutate}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
};

const StatChip = ({ label, value, colorClass, theme }) => (
  <div className={`flex items-baseline gap-1.5 px-3 py-1.5 rounded-md ${theme.chipBg} border ${theme.border}`}>
    <b className={`text-base font-extrabold tabular-nums ${colorClass || theme.text}`}>{value}</b>
    <span className={`text-[11px] uppercase tracking-wide ${theme.textMuted}`}>{label}</span>
  </div>
);

const FilterChip = ({ label, active, onClick, theme }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
      active ? 'bg-blue-600 border-blue-600 text-white' : `${theme.chipBg} ${theme.border} ${theme.textSecondary}`
    }`}
  >
    {label}
  </button>
);

const StatusMapDetail = ({ item, theme, onStatusChange, onDelete, mutating }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  useEffect(() => setTaskCreated(false), [item.id]);
  const c = colorFor(item);

  const handleCreateTask = () => {
    dispatch(createTaskThunk({
      title: `Fix: ${item.title}`,
      description: item.description || null,
      priority: item.severity || 'medium',
      assignee_ids: [],
      source_type: 'architecture_status',
      source_id: item.id,
    })).then((res) => { if (!res.error) setTaskCreated(true); });
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <div className="text-xs font-bold uppercase tracking-wide" style={{ color: '#818cf8' }}>{item.component}</div>
        {item.category === 'regulatory' && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/30">
            {t('admin.statusMap.regulatoryBadge')}
          </span>
        )}
      </div>
      <div className="flex items-start justify-between gap-4 mb-3">
        <h2 className={`text-xl font-extrabold leading-snug ${theme.text}`}>{item.title}</h2>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setStatusMenuOpen((v) => !v)}
            disabled={mutating}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${c.bg} ${c.text} border ${c.border}`}
          >
            {item.status === 'fixed' && <CheckCircle2 className="w-3.5 h-3.5" />}
            {item.status === 'fixed' ? t('admin.statusMap.detail.fixedBadge') : `${item.severity} · ${item.status === 'open' ? t('admin.statusMap.detail.openLabel') : t('admin.statusMap.detail.deferredLabel')}`}
            <ChevronDown className="w-3 h-3" />
          </button>
          {statusMenuOpen && (
            <div className={`absolute right-0 mt-1 z-10 rounded-md border ${theme.border} ${theme.cardBg} shadow-lg py-1 w-36`}>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    onStatusChange(s);
                    setStatusMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1.5 text-sm capitalize ${theme.hoverBg} ${theme.text}`}
                >
                  {s === 'fixed' ? t('admin.statusMap.detail.fixedBadge') : s === 'open' ? t('admin.statusMap.detail.openLabel') : t('admin.statusMap.detail.deferredLabel')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className={`text-sm leading-relaxed ${theme.text} mb-4`}>{item.summary}</p>

      {item.detail && (
        <div className="mb-4">
          <h4 className={`text-[11px] font-bold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>{t('admin.statusMap.detail.detailHeading')}</h4>
          <p className={`text-sm leading-relaxed ${theme.textSecondary}`}>{item.detail}</p>
        </div>
      )}

      {item.fix_text && (
        <div className="mb-4">
          <h4 className={`text-[11px] font-bold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>
            {item.status === 'fixed' ? t('admin.statusMap.detail.whatWasDone') : t('admin.statusMap.detail.proposedFix')}
          </h4>
          <p className={`text-sm leading-relaxed ${theme.textSecondary}`}>{item.fix_text}</p>
        </div>
      )}

      {item.files && item.files.length > 0 && (
        <div className="mb-4">
          <h4 className={`text-[11px] font-bold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>{t('admin.statusMap.detail.filesHeading')}</h4>
          <div className="flex flex-col gap-1">
            {item.files.map((f, idx) => (
              <span key={idx} className={`text-xs font-mono px-2 py-1 rounded w-fit ${theme.chipBg} ${theme.textSecondary}`}>{f}</span>
            ))}
          </div>
        </div>
      )}

      <div className={`flex items-center justify-between pt-3 mt-2 border-t ${theme.border}`}>
        <span className={`text-xs font-mono ${theme.textMuted}`}>{t('admin.statusMap.detail.sourceLabel', { source: item.source || t('admin.statusMap.detail.sourceUnknown') })}</span>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCreateTask}
            disabled={taskCreated}
            className="flex items-center gap-1 text-xs font-medium text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
          >
            {taskCreated ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <ClipboardList className="w-3.5 h-3.5" />}
            {t('admin.statusMap.detail.createTask')}
          </button>
          <button
            onClick={onDelete}
            disabled={mutating}
            className="flex items-center gap-1 text-xs font-medium text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('admin.statusMap.detail.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminStatusMapComponent;
